import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Briefcase, Save, X, Sparkles, Wand2, Zap, Award, CheckCircle, Star,
  Eye, EyeOff, Calendar, MapPin, Building, ClipboardPaste, Loader2, RefreshCw,
  LayoutGrid, FileText, ChevronRight, Info
} from 'lucide-react';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../ui/jewel-modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const JOB_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Volunteer', 'Apprenticeship'];
const EXP_LEVELS = ['Entry Level', 'Mid Level', 'Senior', 'Lead', 'Director'];

const EMPTY_FORM = {
  title: '', department: '', location: '', type: 'Full-time', description: '',
  salary_range: '', experience_level: '', requirements: [], responsibilities: [],
  benefits: [], application_deadline: '', is_urgent: false, is_active: true, slug: ''
};

/**
 * Premium vacancy composer — Jewel & Luxury theme.
 * Features:
 *   • Split into scannable sections (basics · description · lists · publish)
 *   • AI-assist buttons on description / requirements / responsibilities / benefits
 *   • Bulk-paste on list items (one per line auto-splits)
 *   • Live preview toggle
 *   • Duplicate-friendly (accepts prefilled form)
 */
export const VacancyComposer = ({ open, onOpenChange, editing, getAuthHeaders, onSaved, prefill }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [aiBusy, setAiBusy] = useState(null); // 'description' | 'polish' | 'requirements' | ...

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: editing.title || '',
        department: editing.department || '',
        location: editing.location || '',
        type: editing.type || 'Full-time',
        description: editing.description || '',
        salary_range: editing.salary_range || '',
        experience_level: editing.experience_level || '',
        requirements: editing.requirements || [],
        responsibilities: editing.responsibilities || [],
        benefits: editing.benefits || [],
        application_deadline: editing.application_deadline || '',
        is_urgent: !!editing.is_urgent,
        is_active: editing.is_active !== false,
        slug: editing.slug || '',
      });
    } else if (prefill) {
      setForm({ ...EMPTY_FORM, ...prefill });
    } else {
      setForm(EMPTY_FORM);
    }
    setShowPreview(false);
  }, [open, editing, prefill]);

  // Auto-generate slug from title if empty
  const autoSlug = useMemo(() => {
    if (form.slug) return form.slug;
    return (form.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }, [form.title, form.slug]);

  const save = async () => {
    if (!form.title.trim() || !form.department.trim()) {
      toast.error('Title and department are required');
      return;
    }
    setBusy(true);
    try {
      const headers = getAuthHeaders();
      const payload = {
        ...form,
        slug: form.slug || autoSlug,
        requirements: form.requirements.length ? form.requirements : null,
        responsibilities: form.responsibilities.length ? form.responsibilities : null,
        benefits: form.benefits.length ? form.benefits : null,
      };
      if (editing?.id) {
        await axios.put(`${BACKEND_URL}/api/admin/vacancies/${editing.id}`, payload, { headers });
        toast.success('Vacancy updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/vacancies`, payload, { headers });
        toast.success(form.is_active ? 'Vacancy published' : 'Draft saved');
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setBusy(false);
  };

  const runAI = async (mode) => {
    if (!form.title.trim()) {
      toast.error('Enter a job title first — AI needs context');
      return;
    }
    setAiBusy(mode);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/vacancies/ai-assist`, {
        mode,
        title: form.title,
        department: form.department,
        experience_level: form.experience_level,
        current_description: form.description,
        context: `Type: ${form.type}, Location: ${form.location}`,
      }, { headers });
      if (mode === 'description' || mode === 'polish') {
        setForm(f => ({ ...f, description: res.data.text }));
        toast.success(mode === 'polish' ? 'Description polished' : 'Description generated');
      } else {
        setForm(f => ({ ...f, [mode]: [...(f[mode] || []), ...(res.data.items || [])] }));
        toast.success(`${(res.data.items || []).length} ${mode} added`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI assist failed');
    }
    setAiBusy(null);
  };

  const bulkPaste = (field) => {
    const raw = window.prompt(`Paste ${field} — one per line:`);
    if (!raw) return;
    const items = raw.split('\n').map(s => s.trim().replace(/^[-*•·]\s*/, '').replace(/^\d+[.)]\s*/, '')).filter(Boolean);
    if (items.length === 0) return;
    setForm(f => ({ ...f, [field]: [...(f[field] || []), ...items] }));
    toast.success(`${items.length} added to ${field}`);
  };

  const addItem = (field, value) => {
    if (!value.trim()) return;
    setForm(f => ({ ...f, [field]: [...(f[field] || []), value.trim()] }));
  };
  const removeItem = (field, idx) => {
    setForm(f => ({ ...f, [field]: (f[field] || []).filter((_, i) => i !== idx) }));
  };

  const sectionTone = (hex) => ({
    background: `linear-gradient(135deg, ${hex}0A, transparent)`,
    borderColor: `${hex}25`,
  });

  return (
    <JewelModal
      open={open}
      onOpenChange={onOpenChange}
      icon={editing ? Save : Sparkles}
      title={editing ? 'Edit vacancy' : 'New vacancy'}
      subtitle={editing ? 'Update role details' : 'Compose a new opening — with AI where helpful'}
      accent="cyan"
      size="3xl"
      testid="vacancy-composer"
      footer={<>
        <JewelButton variant="ghost" onClick={() => onOpenChange(false)}>Cancel</JewelButton>
        <JewelButton variant="ghost" onClick={() => setShowPreview(p => !p)} icon={showPreview ? FileText : Eye} data-testid="vacancy-preview-toggle">
          {showPreview ? 'Edit' : 'Preview'}
        </JewelButton>
        {!form.is_active && (
          <JewelButton variant="ghost" onClick={() => setForm(f => ({ ...f, is_active: true }))}>
            Publish as active
          </JewelButton>
        )}
        <JewelButton accent="cyan" onClick={save} loading={busy} disabled={busy} icon={Save} data-testid="vacancy-save-btn">
          {editing ? 'Update' : (form.is_active ? 'Publish' : 'Save draft')}
        </JewelButton>
      </>}
    >
      {showPreview ? (
        <VacancyPreview form={form} />
      ) : (
        <div className="space-y-4">
          {/* ── BASICS ─────────────────────────────────────── */}
          <section className="relative overflow-hidden rounded-2xl border p-4" style={sectionTone('#00E5FF')}>
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="w-3.5 h-3.5 text-cyan-300" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-cyan-200/80 font-semibold">Basics</p>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <JewelField label="Job title" required>
                  <JewelInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Senior Operations Manager" data-testid="vacancy-title-input" autoFocus />
                </JewelField>
                <JewelField label="Department" required>
                  <JewelInput value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}
                    placeholder="e.g. Operations" data-testid="vacancy-dept-input" />
                </JewelField>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <JewelField label="Location">
                  <JewelInput value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Remote · London · Hybrid" icon={MapPin} data-testid="vacancy-location-input" />
                </JewelField>
                <JewelField label="Job type">
                  <JewelSelect value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="vacancy-type-input">
                    {JOB_TYPES.map(t => <option key={t}>{t}</option>)}
                  </JewelSelect>
                </JewelField>
                <JewelField label="Experience">
                  <JewelSelect value={form.experience_level} onChange={(e) => setForm({ ...form, experience_level: e.target.value })} data-testid="vacancy-exp-input">
                    <option value="">Any</option>
                    {EXP_LEVELS.map(l => <option key={l}>{l}</option>)}
                  </JewelSelect>
                </JewelField>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <JewelField label="Salary range">
                  <JewelInput value={form.salary_range} onChange={(e) => setForm({ ...form, salary_range: e.target.value })}
                    placeholder="e.g. £45,000 – £60,000 · Competitive · DOE" />
                </JewelField>
                <JewelField label="Application deadline">
                  <JewelInput type="date" value={form.application_deadline} onChange={(e) => setForm({ ...form, application_deadline: e.target.value })}
                    data-testid="vacancy-deadline-input" />
                </JewelField>
              </div>
              <JewelField label="Public URL slug" hint={form.slug ? '' : `auto: /vacancies/${autoSlug}`}>
                <JewelInput value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder={autoSlug || 'senior-operations-manager'} />
              </JewelField>
            </div>
          </section>

          {/* ── DESCRIPTION with AI ─────────────────────── */}
          <section className="relative overflow-hidden rounded-2xl border p-4" style={sectionTone('#8A2BE2')}>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-violet-300" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-violet-200/80 font-semibold">Description</p>
              </div>
              <div className="flex items-center gap-1.5">
                <AIButton mode="description" busy={aiBusy} onClick={() => runAI('description')}
                  icon={Wand2} label={form.description ? 'Regenerate' : 'AI generate'} />
                {form.description && (
                  <AIButton mode="polish" busy={aiBusy} onClick={() => runAI('polish')}
                    icon={Sparkles} label="Polish" />
                )}
              </div>
            </div>
            <JewelTextarea rows={6} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe the role, the team, and why someone would love this job. Tap 'AI generate' above to draft this from your title & department." />
            <p className="text-white/35 text-[10px] mt-1.5 flex items-center gap-1"><Info className="w-2.5 h-2.5" /> {form.description.length} chars · aim for 400-900</p>
          </section>

          {/* ── LISTS: Requirements / Responsibilities / Benefits ─────── */}
          {[
            { field: 'requirements',     label: 'Requirements',      hex: '#F43F5E', icon: Award,       placeholder: '5+ years managing operations' },
            { field: 'responsibilities', label: 'Responsibilities',  hex: '#10B981', icon: CheckCircle, placeholder: 'Lead a team of 8 on new launches' },
            { field: 'benefits',         label: 'Benefits',          hex: '#F59E0B', icon: Star,        placeholder: '25 days paid leave + bank holidays' },
          ].map(({ field, label, hex, icon: Icon, placeholder }) => (
            <ListSection key={field}
              field={field} label={label} hex={hex} Icon={Icon} placeholder={placeholder}
              items={form[field] || []}
              onAdd={(v) => addItem(field, v)}
              onRemove={(i) => removeItem(field, i)}
              onBulkPaste={() => bulkPaste(field)}
              aiBusy={aiBusy}
              onAI={() => runAI(field)}
            />
          ))}

          {/* ── PUBLISH ────────────────────────────── */}
          <section className="relative overflow-hidden rounded-2xl border p-4" style={sectionTone('#EC4899')}>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-3.5 h-3.5 text-rose-300" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-rose-200/80 font-semibold">Publish</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <ToggleChip active={form.is_active} onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                hex="#10B981" activeIcon={Eye} inactiveIcon={EyeOff}
                activeLabel="Live on public page" inactiveLabel="Save as draft"
                testid="vacancy-active-toggle" />
              <ToggleChip active={form.is_urgent} onClick={() => setForm(f => ({ ...f, is_urgent: !f.is_urgent }))}
                hex="#F43F5E" activeIcon={Zap} inactiveIcon={Zap}
                activeLabel="Marked as urgent" inactiveLabel="Mark as urgent"
                testid="vacancy-urgent-toggle" />
            </div>
          </section>
        </div>
      )}
    </JewelModal>
  );
};

/* Reusable pieces */

const AIButton = ({ mode, busy, onClick, icon: Icon, label }) => {
  const isBusy = busy === mode;
  return (
    <button type="button" onClick={onClick} disabled={!!busy}
      data-testid={`ai-assist-${mode}`}
      className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-400/30 text-violet-100 hover:from-violet-500/35 hover:to-cyan-500/35 hover:border-violet-400/50 disabled:opacity-50 disabled:cursor-not-allowed text-[10.5px] font-semibold transition-all">
      {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      <span>{label}</span>
      <Sparkles className="w-2.5 h-2.5 text-amber-300 opacity-70 group-hover:opacity-100" />
    </button>
  );
};

const ToggleChip = ({ active, onClick, hex, activeIcon: AI, inactiveIcon: II, activeLabel, inactiveLabel, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-[11px] font-medium transition-all"
    style={{
      background: active ? `${hex}18` : 'rgba(255,255,255,0.03)',
      borderColor: active ? `${hex}55` : 'rgba(255,255,255,0.08)',
      color: active ? hex : 'rgba(255,255,255,0.55)',
      boxShadow: active ? `0 0 20px -8px ${hex}` : 'none',
    }}>
    {active ? <AI className="w-3.5 h-3.5" /> : <II className="w-3.5 h-3.5" />}
    {active ? activeLabel : inactiveLabel}
  </button>
);

const ListSection = ({ field, label, hex, Icon, placeholder, items, onAdd, onRemove, onBulkPaste, aiBusy, onAI }) => {
  const [input, setInput] = useState('');
  const handleAdd = () => { if (input.trim()) { onAdd(input); setInput(''); } };
  return (
    <section className="relative overflow-hidden rounded-2xl border p-4"
      style={{ background: `linear-gradient(135deg, ${hex}0A, transparent)`, borderColor: `${hex}25` }}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" style={{ color: hex }} />
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: `${hex}DD` }}>{label}</p>
          {items.length > 0 && <span className="text-white/40 text-[10px]">· {items.length}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={onBulkPaste} data-testid={`bulk-paste-${field}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/70 hover:bg-white/[0.08] hover:text-white text-[10px] font-medium transition-all">
            <ClipboardPaste className="w-2.5 h-2.5" /> Paste list
          </button>
          <AIButton mode={field} busy={aiBusy} onClick={onAI} icon={Wand2} label="AI suggest" />
        </div>
      </div>
      <div className="flex gap-2 mb-2">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          placeholder={placeholder}
          data-testid={`list-input-${field}`}
          className="flex-1 px-3 py-2 bg-black/40 border border-white/[0.06] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-400/40" />
        <button type="button" onClick={handleAdd} data-testid={`list-add-${field}`}
          className="px-3 py-2 rounded-xl border text-[11px] font-semibold transition-all"
          style={{ background: `${hex}15`, borderColor: `${hex}40`, color: hex }}>Add</button>
      </div>
      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm border"
              style={{ background: `${hex}0F`, borderColor: `${hex}25` }}>
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: hex }} />
              <span className="flex-1 text-white/85">{item}</span>
              <button type="button" onClick={() => onRemove(idx)}
                data-testid={`list-remove-${field}-${idx}`}
                className="w-5 h-5 rounded-md flex items-center justify-center text-white/40 hover:text-rose-300 hover:bg-rose-500/20">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const VacancyPreview = ({ form }) => (
  <div className="rounded-2xl border border-cyan-500/25 bg-gradient-to-br from-cyan-500/[0.05] via-[#0a0a14] to-violet-500/[0.05] p-6" data-testid="vacancy-preview-panel">
    <div className="flex items-center gap-2 flex-wrap mb-3">
      {form.is_urgent && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-400/40 text-rose-200 text-[10px] font-semibold uppercase tracking-wider">Urgent</span>}
      {!form.is_active && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/[0.10] text-white/60 text-[10px] font-semibold uppercase tracking-wider">Draft</span>}
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 text-[10px] font-semibold uppercase tracking-wider">{form.type}</span>
      {form.experience_level && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/15 border border-violet-400/30 text-violet-200 text-[10px] font-semibold uppercase tracking-wider">{form.experience_level}</span>}
    </div>
    <h2 className="text-3xl font-semibold text-white tracking-tight">{form.title || <span className="text-white/30 italic">Untitled role</span>}</h2>
    <div className="flex items-center gap-4 flex-wrap mt-2 text-white/60 text-sm">
      {form.department && <span className="flex items-center gap-1.5"><Building className="w-3.5 h-3.5" />{form.department}</span>}
      {form.location && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{form.location}</span>}
      {form.salary_range && <span className="flex items-center gap-1.5 text-emerald-300">£ {form.salary_range}</span>}
      {form.application_deadline && <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Apply by {new Date(form.application_deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
    </div>
    {form.description && (
      <div className="mt-4 pt-4 border-t border-white/[0.06]">
        <p className="text-white/80 text-[13.5px] leading-relaxed whitespace-pre-wrap">{form.description}</p>
      </div>
    )}
    {form.responsibilities?.length > 0 && (
      <PreviewList title="What you'll do" items={form.responsibilities} hex="#10B981" Icon={CheckCircle} />
    )}
    {form.requirements?.length > 0 && (
      <PreviewList title="What we're looking for" items={form.requirements} hex="#F43F5E" Icon={Award} />
    )}
    {form.benefits?.length > 0 && (
      <PreviewList title="What you'll get" items={form.benefits} hex="#F59E0B" Icon={Star} />
    )}
  </div>
);

const PreviewList = ({ title, items, hex, Icon }) => (
  <div className="mt-5">
    <p className="text-[10px] tracking-[0.2em] uppercase font-semibold mb-2" style={{ color: hex }}>{title}</p>
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
          <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: hex }} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default VacancyComposer;
